/**
 * CollaborationMetricsCard Component
 * Displays collaboration activity metrics
 *
 * Story 3.4: Collaboration Metrics
 */

import React from 'react';
import { clsx } from 'clsx';
import { MessageSquare, Users, TrendingUp, Target } from 'lucide-react';
import { Card, Badge } from '../../common';
import { ScopeBadge } from '../ReportScopeIndicator';
import type { ResolvedScope } from '../../../hooks/useReportData';

export interface CollaborationData {
  totalComments: number;
  avgCommentsPerOKR: number;
  topContributors: Array<{ userId: string; name: string; count: number }>;
  mostDiscussedOKRs: Array<{ okrId: string; title: string; commentCount: number }>;
}

export interface CollaborationMetricsCardProps {
  /** Collaboration data */
  data: CollaborationData;
  /** Loading state */
  isLoading?: boolean;
  /**
   * ⚠️ Scope of the LISTS — `topContributors` and `mostDiscussedOKRs`.
   *
   * This card is PARTIALLY scoped by design (spec § 4.3): the response carries
   * a single `scope` field saying `team`, but `totalComments` and
   * `avgCommentsPerOKR` are still organization-wide — they ARE the org
   * baseline, which is why this endpoint has no separate `orgBaseline`.
   * Labelling the whole card "your team" would misdescribe half of it, so the
   * two halves take separate labels.
   */
  listScope?: ResolvedScope;
  /** Scope of `totalComments` / `avgCommentsPerOKR` — see `listScope`. */
  totalsScope?: ResolvedScope;
  /** Click handler for contributor */
  onContributorClick?: (userId: string) => void;
  /** Click handler for OKR */
  onOKRClick?: (okrId: string) => void;
  /** Additional class names */
  className?: string;
}

/**
 * Loading skeleton
 */
const CollaborationSkeleton: React.FC = () => (
  <div className="animate-pulse space-y-6">
    <div className="grid grid-cols-2 gap-4">
      {[1, 2].map((i) => (
        <div key={i} className="p-4 bg-stone-50 rounded-lg">
          <div className="h-8 bg-stone-200 rounded w-16 mb-2" />
          <div className="h-3 bg-stone-200 rounded w-24" />
        </div>
      ))}
    </div>
    <div>
      <div className="h-4 bg-stone-200 rounded w-32 mb-3" />
      <div className="space-y-2">
        {[1, 2, 3].map((i) => (
          <div key={i} className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-stone-200" />
            <div className="flex-1 h-4 bg-stone-200 rounded" />
          </div>
        ))}
      </div>
    </div>
  </div>
);

/**
 * Collaboration Metrics Card Component
 */
export const CollaborationMetricsCard: React.FC<CollaborationMetricsCardProps> = ({
  data,
  isLoading = false,
  listScope,
  totalsScope,
  onContributorClick,
  onOKRClick,
  className,
}) => {
  if (isLoading) {
    return (
      <Card className={clsx('p-6', className)}>
        <h3 className="text-lg font-semibold text-stone-900 mb-4">Collaboration</h3>
        <CollaborationSkeleton />
      </Card>
    );
  }

  return (
    <Card className={clsx('p-6', className)}>
      <h3 className="text-lg font-semibold text-stone-900 mb-4">Collaboration</h3>

      {/* Summary stats — organization-wide even for a scoped caller */}
      {totalsScope && (
        <div className="mb-2 flex justify-end">
          <ScopeBadge scope={totalsScope} />
        </div>
      )}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="p-4 bg-primary-50 rounded-lg">
          <div className="flex items-center gap-2 mb-1">
            <MessageSquare className="w-5 h-5 text-primary-600" />
            <span className="text-2xl font-bold text-primary-700">{data.totalComments}</span>
          </div>
          <p className="text-sm text-primary-600">Total Comments</p>
        </div>
        <div className="p-4 bg-green-50 rounded-lg">
          <div className="flex items-center gap-2 mb-1">
            <TrendingUp className="w-5 h-5 text-green-600" />
            <span className="text-2xl font-bold text-green-700">{data.avgCommentsPerOKR}</span>
          </div>
          <p className="text-sm text-green-600">Avg per OKR</p>
        </div>
      </div>

      {/* Top contributors — the scoped half */}
      {data.topContributors.length > 0 && (
        <div className="mb-6">
          <h4 className="text-sm font-medium text-stone-700 mb-3 flex flex-wrap items-center gap-2">
            <Users className="w-4 h-4" />
            Top Contributors
            {listScope && <ScopeBadge scope={listScope} />}
          </h4>
          <div className="space-y-2">
            {data.topContributors.slice(0, 5).map((contributor, index) => (
              <div
                key={contributor.userId}
                onClick={() => onContributorClick?.(contributor.userId)}
                className={clsx(
                  'flex items-center gap-3 p-2 rounded-lg',
                  onContributorClick && 'cursor-pointer hover:bg-stone-50'
                )}
              >
                <div className={clsx(
                  'w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium',
                  index === 0 ? 'bg-amber-100 text-amber-700' :
                  index === 1 ? 'bg-stone-200 text-stone-700' :
                  index === 2 ? 'bg-orange-100 text-orange-700' :
                  'bg-stone-100 text-stone-600'
                )}>
                  {index + 1}
                </div>
                <span className="flex-1 text-sm text-stone-900 truncate">{contributor.name}</span>
                <Badge variant="secondary" size="sm">{contributor.count} comments</Badge>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Most discussed OKRs — the scoped half */}
      {data.mostDiscussedOKRs.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-stone-700 mb-3 flex flex-wrap items-center gap-2">
            <Target className="w-4 h-4" />
            Most Discussed OKRs
            {listScope && <ScopeBadge scope={listScope} />}
          </h4>
          <div className="space-y-2">
            {data.mostDiscussedOKRs.slice(0, 3).map((okr) => (
              <div
                key={okr.okrId}
                onClick={() => onOKRClick?.(okr.okrId)}
                className={clsx(
                  'p-3 bg-stone-50 rounded-lg',
                  onOKRClick && 'cursor-pointer hover:bg-stone-100'
                )}
              >
                <div className="flex items-start justify-between gap-2">
                  <p className="text-sm text-stone-900 line-clamp-2">{okr.title}</p>
                  <Badge variant="primary" size="sm" className="flex-shrink-0">
                    {okr.commentCount}
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty states.
          Two distinct cases, because the totals and the lists cover different
          populations for a scoped caller: the organization can be busy while
          the caller's own team has commented on nothing. Keying the only empty
          state off `totalComments` would leave that case as a silently blank
          card. */}
      {data.totalComments === 0 ? (
        <div className="text-center py-8 text-stone-500">
          <MessageSquare className="w-12 h-12 mx-auto mb-3 text-stone-300" />
          <p>No collaboration activity yet</p>
        </div>
      ) : (
        data.topContributors.length === 0 &&
        data.mostDiscussedOKRs.length === 0 && (
          <div className="text-center py-8 text-stone-500">
            <MessageSquare className="w-12 h-12 mx-auto mb-3 text-stone-300" />
            <p>
              {listScope?.isSelfOnly
                ? 'No comments on your own OKRs in this period'
                : listScope?.kind === 'team'
                  ? 'No comments from your team in this period'
                  : 'No comments in this period'}
            </p>
          </div>
        )
      )}
    </Card>
  );
};

export default CollaborationMetricsCard;
